/**
 * DetailsCard - Card "Detalhes" da sidebar T1 (Etapa, Prioridade, Categoria,
 * Data limite, Tags).
 *
 * Popovers de Etapa/Prioridade e a lógica de tags são portados 1:1 do
 * `TicketDetailSidebar` legado (mesmos handlers recebidos via props da
 * página) — só a casca visual muda (agrupado em card compacto).
 */
import React, { useState } from 'react';
import { useTagStyle } from '@/shared/hooks/useTagCatalog';
import { Input } from '@/shared/components/ui';
import { SidebarCard } from './SidebarCard';
import { SidebarField } from '../SidebarField';
import { TicketFieldPopover } from '../TicketFieldPopover';
import type { PopulatedTicket, TicketPriority, TicketStageResponse, TicketStatusCategory } from '../../types';
import { TICKET_PRIORITY, TICKET_STATUS_CATEGORIES } from '../../types';
import { Icon } from '@/shared/icons';

export interface DetailsCardProps {
  ticket: PopulatedTicket;
  stages: TicketStageResponse[];
  onStageChange: (stageId: string) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onStageChangeWithConfirmation: (stageId: string, stage: TicketStageResponse) => void;
  /** Adiciona a tag informada — o card cuida do input inline e do dedup. */
  onAddTag?: (tag: string) => void;
  onRemoveTag?: (tag: string) => void;
  loading?: boolean;
}

export const DetailsCard: React.FC<DetailsCardProps> = ({
  ticket,
  stages,
  onStageChange,
  onPriorityChange,
  onStageChangeWithConfirmation,
  onAddTag,
  onRemoveTag,
  loading = false,
}) => {
  // Cor de catálogo (22/09/2026). Era roxo fixo para toda tag — o mesmo
  // roxo que o chat usava, então nenhuma tag se distinguia de outra.
  const tagStyle = useTagStyle();
  const [activePopover, setActivePopover] = useState<'stage' | 'priority' | null>(null);

  // Add-tag inline (mesmo padrão de input+Enter do TicketFormDrawer)
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const currentStage = ticket.stage || stages.find((s) => s.id === ticket.stageId);
  const currentCategory = currentStage?.category || ticket.statusCategory;

  const submitTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;
    if (!(ticket.tags || []).includes(trimmed)) {
      onAddTag?.(trimmed);
    }
    setTagInput('');
    setIsAddingTag(false);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitTag();
    } else if (e.key === 'Escape') {
      setTagInput('');
      setIsAddingTag(false);
    }
  };

  const togglePopover = (name: 'stage' | 'priority') => {
    setActivePopover((prev) => (prev === name ? null : name));
  };

  // Agrupa stages por categoria (mesma ordem/config do sidebar legado)
  const categoryOrder: TicketStatusCategory[] = ['new', 'in_progress', 'waiting', 'resolved', 'closed'];
  const stagesByCategory = categoryOrder
    .map((cat) => ({
      category: cat,
      config: TICKET_STATUS_CATEGORIES[cat],
      stages: stages.filter((s) => s.category === cat),
    }))
    .filter((group) => group.stages.length > 0);

  const handleStageSelect = (stage: TicketStageResponse) => {
    setActivePopover(null);
    if (stage.category !== currentCategory) {
      onStageChangeWithConfirmation(stage.id, stage);
    } else {
      onStageChange(stage.id);
    }
  };

  const formatDueDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  return (
    <SidebarCard icon="list" title="Detalhes" collapsible data-testid="ticket-detail-details-card">
      {/* Stage */}
      <div className="relative">
        <SidebarField
          label="Etapa"
          clickable
          onClick={() => togglePopover('stage')}
          ariaLabel={`Etapa: ${currentStage?.name || ticket.status || '—'}`}
          data-testid="ticket-detail-stage-field"
        >
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: currentStage?.color || '#94a3b8' }}
          />
          <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
            {currentStage?.name || ticket.status || '—'}
          </span>
        </SidebarField>

        <TicketFieldPopover isOpen={activePopover === 'stage'} onClose={() => setActivePopover(null)} width={240}>
          <div className="py-1 max-h-64 overflow-y-auto">
            {stagesByCategory.map((group) => (
              <div key={group.category}>
                <div
                  className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: group.config.color }}
                >
                  {group.config.label}
                </div>
                {group.stages.map((stage) => (
                  <button
                    key={stage.id}
                    data-testid={`ticket-detail-stage-option-${stage.id}`}
                    className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                    style={{ color: '#1e293b' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    onClick={() => handleStageSelect(stage)}
                    disabled={loading}
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                    <span className="flex-1">{stage.name}</span>
                    {stage.id === (currentStage?.id || ticket.stageId) && (
                      <Icon name="check" className="w-3 h-3" style={{ color: '#8b5cf6' }} />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </TicketFieldPopover>
      </div>

      {/* Priority */}
      <div className="relative">
        <SidebarField
          label="Prioridade"
          clickable
          onClick={() => togglePopover('priority')}
          ariaLabel={`Prioridade: ${TICKET_PRIORITY[ticket.priority as TicketPriority]?.label || ticket.priority}`}
          data-testid="ticket-detail-priority-field"
        >
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: TICKET_PRIORITY[ticket.priority as TicketPriority]?.color || '#94a3b8' }}
          />
          <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
            {TICKET_PRIORITY[ticket.priority as TicketPriority]?.label || ticket.priority}
          </span>
        </SidebarField>

        <TicketFieldPopover isOpen={activePopover === 'priority'} onClose={() => setActivePopover(null)}>
          <div className="py-1">
            {(Object.entries(TICKET_PRIORITY) as [TicketPriority, { label: string; color: string }][]).map(
              ([key, config]) => (
                <button
                  key={key}
                  data-testid={`ticket-detail-priority-option-${key}`}
                  className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                  style={{ color: '#1e293b' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  onClick={() => { onPriorityChange(key); setActivePopover(null); }}
                  disabled={loading}
                >
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: config.color }} />
                  <span className="flex-1">{config.label}</span>
                  {ticket.priority === key && <Icon name="check" className="w-3 h-3" style={{ color: '#8b5cf6' }} />}
                </button>
              )
            )}
          </div>
        </TicketFieldPopover>
      </div>

      {/* Category — read-only for now (mesmo comportamento do sidebar legado) */}
      <SidebarField label="Categoria" data-testid="ticket-detail-category-field">
        <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
          {ticket.category || '—'}
        </span>
      </SidebarField>

      {/* Data limite — read-only (edição via formulário completo) */}
      <SidebarField label="Data limite" data-testid="ticket-detail-due-date-field">
        <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
          {formatDueDate(ticket.dueDate)}
        </span>
      </SidebarField>

      {/* Origem — só aparecia na antiga aba "Detalhes"; preservada aqui pra não perder a info */}
      {ticket.source && (
        <SidebarField label="Origem" data-testid="ticket-detail-source-field">
          <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
            {ticket.source}
          </span>
        </SidebarField>
      )}

      {/* Tags */}
      <div
        className="py-1.5"
        style={{ borderBottom: '1px solid var(--theme-card-border, #f1f5f9)' }}
      >
        <div className="flex items-start justify-between">
          <span className="text-xs pt-0.5" style={{ color: 'var(--theme-muted-text, #64748b)' }}>
            Tags
          </span>
          <div className="flex flex-wrap gap-1 justify-end max-w-[65%]">
            {ticket.tags && ticket.tags.length > 0 ? (
              ticket.tags.map((tag) => (
                <span
                  key={tag}
                  data-testid={`ticket-detail-tag-${tag}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full border"
                  style={tagStyle(tag)}
                >
                  {tag}
                  {onRemoveTag && (
                    <button
                      onClick={() => onRemoveTag(tag)}
                      className="hover:opacity-70"
                      disabled={loading}
                      aria-label={`Remover tag ${tag}`}
                      data-testid={`ticket-detail-tag-remove-${tag}`}
                    >
                      <Icon name="x" className="w-2.5 h-2.5" />
                    </button>
                  )}
                </span>
              ))
            ) : (
              <span className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>—</span>
            )}
            {onAddTag && (
              <button
                onClick={() => setIsAddingTag((v) => !v)}
                disabled={loading}
                className="text-[11px] font-medium transition-colors"
                style={{ color: '#8b5cf6' }}
                data-testid="ticket-detail-tag-add"
                aria-label="Adicionar tag"
              >
                +
              </button>
            )}
          </div>
        </div>

        {/* Input inline de nova tag (Enter adiciona, Esc cancela) */}
        {onAddTag && isAddingTag && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <Input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              placeholder="Nova tag"
              disabled={loading}
              autoFocus
              className="h-7 text-xs px-2"
              data-testid="ticket-detail-tag-input"
            />
            <button
              onClick={submitTag}
              disabled={loading || !tagInput.trim()}
              className="flex-shrink-0 px-2 py-1 rounded text-[11px] font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--theme-primary)' }}
              data-testid="ticket-detail-tag-submit"
            >
              Adicionar
            </button>
          </div>
        )}
      </div>
    </SidebarCard>
  );
};

DetailsCard.displayName = 'DetailsCard';
